import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose"; // @flow

import React, { useState, useEffect, useRef } from 'react';
import { valuesAreTheSame, splitInput } from './service';
import DefaultTheme from './theme';
/*::
import type { MultiInputThemeType } from './theme';
import type { StyledComponent } from 'styled-components';

type Props = {|
  value?: Array<string>, // array of separated parts of user input (multiness)
  validValue?: RegExp,
  separators?: Array<string>, // list of chars or keyboard keys i.e. Enter
  onChange?: (value: Array<string>) => void,
  onFocus?: (e?: FocusEvent) => void;
  onBlur?: (e?: FocusEvent) => void;
  error?: boolean,
  theme?: MultiInputThemeType,
  rest?: any,
|};
*/

var MultiInput = function MultiInput(_ref
/*: Props */
, ref
/*: StyledComponent */
) {
  var value = _ref.value,
      _ref$validValue = _ref.validValue,
      validValue = _ref$validValue === void 0 ? /[^ ]+/ : _ref$validValue,
      _ref$separators = _ref.separators,
      separators = _ref$separators === void 0 ? [',', 'Enter'] : _ref$separators,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? function () {
    return undefined;
  } : _ref$onChange,
      _ref$onFocus = _ref.onFocus,
      onFocus = _ref$onFocus === void 0 ? function () {
    return undefined;
  } : _ref$onFocus,
      _ref$onBlur = _ref.onBlur,
      onBlur = _ref$onBlur === void 0 ? function () {
    return undefined;
  } : _ref$onBlur,
      _ref$error = _ref.error,
      error = _ref$error === void 0 ? false : _ref$error,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? DefaultTheme : _ref$theme,
      rest = _objectWithoutPropertiesLoose(_ref, ["value", "validValue", "separators", "onChange", "onFocus", "onBlur", "error", "theme"]);

  var _useState = useState({
    userInput: ''
  }),
      userInput = _useState[0].userInput,
      setUserInput = _useState[1];

  var inputRef = ref || useRef
  /**: <null | (HTMLInputElement & { changeCallback: Function })>*/
  (null); // eslint-disable-line

  var _useState2 = useState({
    val: value || []
  }),
      val = _useState2[0].val,
      setVal = _useState2[1];

  useEffect(function () {
    if (value && !valuesAreTheSame(value, val)) {
      setVal({
        val: _toConsumableArray(value)
      });
    }
  }, [value]);

  var deleteValue = function deleteValue(idx
  /*: number */
  ) {
    if (idx === void 0) {
      idx = val.length - 1;
    }

    var part2 = val.slice(idx + 1);
    var part1 = val.slice(0, idx);
    var newval = [].concat(_toConsumableArray(part1), _toConsumableArray(part2));
    setVal({
      val: newval
    });
    onChange(newval); // Gstsby focus fix
    // fix for reach/router in gatsby which according to its documentation:
    // https://reach.tech/router/accessibility
    // Whenever the content of a page changes in response to a user
    // interaction, the focus should be moved to that content; otherwise,
    // users on assistive devices have to search around the page to find what
    // changed–yuck! Without the help of a router, managing focus on route
    // transitions requires a lot effort and knowledge on your part.

    setTimeout(function () {
      return focusInput();
    }, 0);
  };

  var focusInput = function focusInput() {
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  var focusComponent = function focusComponent() {
    focusInput();
    onFocus();
  };

  var blurInput = function blurInput() {
    if (userInput) {
      var newval = [].concat(_toConsumableArray(val), _toConsumableArray(splitInput(userInput, separators)));
      setVal({
        val: newval
      });
      setUserInput({
        userInput: ''
      });
      onChange(newval);
    }

    onBlur();
  };

  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === 'Backspace') {
      if (userInput.length <= 0) {
        deleteValue(val.length - 1);
      }

      return;
    }

    var separatorKey = separators.filter(function (s) {
      return s.length > 1;
    }).find(function (k) {
      return k === e.key;
    });
    var separatorChar = separators.filter(function (s) {
      return s.length === 1;
    }).find(function (k) {
      return k === e.key;
    });

    if ((separatorKey || separatorChar) && userInput) {
      var newval = [].concat(_toConsumableArray(val), _toConsumableArray(splitInput(userInput, separators)));
      setVal({
        val: newval
      });
      onChange(newval);
    }

    if (separatorKey || separatorChar) {
      setTimeout(function () {
        setUserInput({
          userInput: ''
        });
      }, 0);
    }
  };

  var handleChange = function handleChange(e) {
    var newInput = e.currentTarget.value;
    setUserInput({
      userInput: newInput
    });
  };

  var handleValueDelete = function handleValueDelete(e) {
    var number = parseInt(e.currentTarget.getAttribute('data-number'), 10);
    deleteValue(number);
  };

  return /*#__PURE__*/React.createElement(theme.Main, Object.assign({
    tabIndex: "0",
    onKeyDown: handleKeyDown,
    onFocus: focusComponent,
    error: error
  }, rest), val.map(function (v, i) {
    return /*#__PURE__*/React.createElement(theme.Value, {
      isValid: validValue.test(v) // eslint-disable-next-line
      ,
      key: i,
      "data-test": "multi-input-value"
    }, /*#__PURE__*/React.createElement(theme.ValueText, {
      "data-test": "multi-input-value-text"
    }, v), /*#__PURE__*/React.createElement(theme.ValueIcon, {
      onClick: handleValueDelete,
      "data-number": i,
      "data-test": "multi-input-value-value-icon"
    }));
  }), /*#__PURE__*/React.createElement(theme.Input, {
    value: userInput,
    onChange: handleChange,
    onBlur: blurInput,
    ref: inputRef,
    inputLength: userInput.length + 3,
    "data-test": "multi-input-input"
  }));
};

export default /*#__PURE__*/React.forwardRef
/*:: <Props, StyledComponent> */
(MultiInput);